import _                                           from 'underscore';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Label }                                   from 'semantic-ui-react';
import { Cell, Grid }                              from 'styled-css-grid';

import { formatOption }                         from '@src/util';
import { CorvalBusinessFunction }               from '@src/types';
import { QueriedSelectFieldProps, SelectField } from '@src/views/components/forms';
import { useQuery }                             from '@apollo/react-hooks';
import { DecisionEngineQueries }                from '@src/queries';
import { queriesHaveData, queriesLoading }      from '@src/store/_apolloUtil';
import { ProjectQueries }                       from '@src/queries/ProjectQueries';
import { useSystemData }                        from '@src/hooks/useSystemData';
import { BusinessFunctionDot }                  from "@src/views/components/product/BusinessFunctionDot";


interface BusinessFunctionDropdownProps extends QueriedSelectFieldProps {
    loading?: boolean,
    formatOptions?: object,
    projectId?: string,
    allowArchived?: boolean,
    disableArchived?: boolean,
}

const defaultProps: BusinessFunctionDropdownProps = {
    loading:         false,
    formatOptions:   {},
    projectId:       null,
    allowArchived:   false,
    disableArchived: true,
};

const BusinessFunctionDropdown: FC<BusinessFunctionDropdownProps> = ( _props ) => {

    let { formatOptions, projectId, allowArchived, disableArchived, ...props } = _props;

    const [ businessFunctions, setBusinessFunctions ] = useState<CorvalBusinessFunction[]>( [] );

    const isProjectSpecific = !_.isNull( projectId );

    const systemData = useSystemData();

    const [ getSystemBusinessFunctions, getSystemBusinessFunctionsReq ] = systemData.businessFunctionsQuery;

    useEffect( () => {

        getSystemBusinessFunctions();

    }, [] );

    const getProjectBusinessFunctionsReq = useQuery(
        ProjectQueries.queries.GET_PROJECT( `id businessFunctions { ${ DecisionEngineQueries.fragments.BUSINESS_FUNCTIONS.QUERY_RETURN } }` ), {
            variables:   { order: { name: 'ASC' }, id: projectId },
            fetchPolicy: 'cache-and-network',
            skip:        !isProjectSpecific
        } );

    let isLoading = ( props.loading || queriesLoading( getSystemBusinessFunctionsReq ) );

    useEffect( () => {

        if( queriesHaveData( getSystemBusinessFunctionsReq ) && ( isProjectSpecific && queriesHaveData( getProjectBusinessFunctionsReq ) || !isProjectSpecific ) ){

            let [ availableBusinessFunctions, archivedBusinessFunctions ] = _.partition( getSystemBusinessFunctionsReq.data.businessFunctions, businessFunction => ( !businessFunction.isArchived ) ),
                projectBusinessFunctions                                  = isProjectSpecific ? getProjectBusinessFunctionsReq.data.projects[ 0 ].businessFunctions : [];

            // reduce to only project specific business functions
            if( isProjectSpecific ){

                const projectBusinessFunctionIds = _.pluck( projectBusinessFunctions, 'id' );

                availableBusinessFunctions = _.filter( availableBusinessFunctions, ( { id } ) => projectBusinessFunctionIds.includes( id ) );
                archivedBusinessFunctions = allowArchived ? _.filter( archivedBusinessFunctions, ( { id } ) => projectBusinessFunctionIds.includes( id ) ) : [];

            } else {

                archivedBusinessFunctions = allowArchived ? archivedBusinessFunctions : []
            }

            setBusinessFunctions(
                _.union( availableBusinessFunctions, archivedBusinessFunctions )
            );
        }

    }, [ getSystemBusinessFunctionsReq, getProjectBusinessFunctionsReq ] );

    let options = businessFunctions.map( ( businessFunction: CorvalBusinessFunction ) => {

            const component = (
                <div>
                    <Grid columns={ `auto 1fr` } rows={ `auto` } columnGap={ '10px' }>
                        <Cell middle><BusinessFunctionDot businessFunction={ businessFunction }/></Cell>
                        <Cell middle>{ businessFunction.name }</Cell>
                    </Grid>
                </div>
            );

            const itemOptions = {
                group:    businessFunction.isArchived ? 'Archived' : 'Active',
                disabled: disableArchived ? businessFunction.isArchived : false,
            }

            return {
                ...formatOption( businessFunction.id, component, { group: itemOptions.group } ),
                disabled: itemOptions.disabled,
            }
        }
    );

    return (
        <SelectField
            { ...props }
            search
            loading={ isLoading }
            options={ options }
        />
    );
};

BusinessFunctionDropdown.defaultProps = defaultProps;

export { BusinessFunctionDropdown };
